import { Button, Dropdown, Flex, Input, Modal, Select, Checkbox, Typography, Card, message } from "antd"
import { useState } from "react"

const { Text, Title } = Typography;

const ModalExportHTTP = ({ isOpen, setIsOpen, data, lang }) => {
    const [proxyFormat, setProxyFormat] = useState("host:port:user:pass");
    const [showNullPort, setShowNullPort] = useState(false);

    const handleCancel = () => {
        setIsOpen(false);
    };

    const formatProxy = (key, format) => {
        let url = key.url;
        if (!url) url = key.ip;

        switch (format) {
            case "host:port:user:pass":
                return `${url}:${key.port}:${key.username}:${key.password}`;
            case "host:port@user:pass":
                return `${url}:${key.port}@${key.username}:${key.password}`;
            case "user:pass:host:port":
                return `${key.username}:${key.password}:${url}:${key.port}`;
            case "user:pass@host:port":
                return `${key.username}:${key.password}@${url}:${key.port}`;
            case "host:port":
                return `${url}:${key.port}`;
            default:
                return `${url}:${key.port}:${key.username}:${key.password}`;
        }
    };

    // Filter data based on status and port condition
    const filteredData = data.filter((key) => {
        if (!key.status) return false;
        if (!showNullPort && (key.port === null || key.port === undefined || key.port === '')) {
            return false;
        }
        return true;
    });

    const keys = filteredData
        .map((key) => formatProxy(key, proxyFormat))
        .join('\n');

    const handleDownload = (format) => {
        let fileContent = "";
        let fileType = "text/plain";
        let fileExtension = "txt";

        if (format === "json") {
            const keyData = filteredData.map((key) => ({
                proxy: formatProxy(key, proxyFormat),
                username: key.username,
                password: key.password,
                host: key.url || key.ip,
                port: key.port
            }));
            fileContent = JSON.stringify(keyData, null, 2);
            fileType = "application/json";
            fileExtension = "json";
        } else if (format === "csv") {
            const csvHeaders = "proxy,username,password,host,port\n";
            const csvRows = filteredData
                .map((key) => {
                    const proxy = formatProxy(key, proxyFormat);
                    return `"${proxy}","${key.username}","${key.password}","${key.url || key.ip}","${key.port}"`;
                })
                .join("\n");
            fileContent = csvHeaders + csvRows;
            fileType = "text/csv";
            fileExtension = "csv";
        } else {
            fileContent = keys;
        }

        const blob = new Blob([fileContent], { type: fileType });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `keys.${fileExtension}`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleCopyKeys = () => {
        navigator.clipboard.writeText(keys).then(() => {
            message.success(lang?.copy_success || "Đã copy danh sách proxy vào clipboard!");
        }).catch(() => {
            message.error(lang?.copy_error || "Không thể copy. Vui lòng thử lại!");
        });
    };

    const formatOptions = [
        { value: "host:port:user:pass", label: "host:port:user:pass" },
        { value: "host:port@user:pass", label: "host:port@user:pass" },
        { value: "user:pass:host:port", label: "user:pass:host:port" },
        { value: "user:pass@host:port", label: "user:pass@host:port" },
        { value: "host:port", label: "host:port" },
    ];

    const items = [
        {
            label: <a onClick={() => handleDownload("txt")}>{lang.download_txt || "Tải xuống TXT"}</a>,
            key: '0',
        },
        {
            label: <a onClick={() => handleDownload("json")}>{lang.download_json || "Tải xuống JSON"}</a>,
            key: '1',
        },
        {
            label: <a onClick={() => handleDownload("csv")}>{lang.download_csv || "Tải xuống CSV"}</a>,
            key: '2',
        },
    ];

    return (
        <Modal
            width={800}
            title={
                <Title level={4} style={{ margin: 0 }}>
                    📋 {lang.export_http_title || "Xuất Key Type HTTP"}
                </Title>
            }
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
        >
            <div style={{ padding: '8px 0' }}>
                {/* Configuration Section */}
                <Card 
                    size="small" 
                    title={`⚙️ ${lang.export_config || "Cấu hình xuất file"}`}
                    style={{ marginBottom: 16 }}
                >
                    <Flex vertical gap={12}>
                        <Flex justify="space-between" align="center">
                            <Text strong>{lang.proxy_format || "Format proxy"}:</Text>
                            <Select
                                value={proxyFormat}
                                onChange={setProxyFormat}
                                options={formatOptions}
                                style={{ width: 250 }}
                                placeholder={lang.select_proxy_format || "Chọn format proxy"}
                            />
                        </Flex>

                        <Flex justify="space-between" align="center">
                            <Text strong>{lang.filter_options || "Tùy chọn filter"}:</Text>
                            <Checkbox
                                checked={showNullPort}
                                onChange={(e) => setShowNullPort(e.target.checked)}
                            >
                                {lang.include_null_port || "Bao gồm proxy có port null"}
                            </Checkbox>
                        </Flex>
                    </Flex>
                </Card>

                {/* Export Section */}
                <Card 
                    size="small"
                    title={
                        <Flex justify="space-between" align="center">
                            <Text strong>
                                📄 {lang.key_list || "Danh sách key"} ({filteredData.length} {lang.items || "items"})
                            </Text>
                            <Flex gap={8}>
                                <Button 
                                    type="default" 
                                    size="middle"
                                    onClick={handleCopyKeys}
                                    disabled={filteredData.length === 0}
                                >
                                    📋 {lang?.copy || "Copy"}
                                </Button>
                                <Dropdown
                                    menu={{ items }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button type="primary" size="middle">
                                        ⬇️ {lang.download || "Tải xuống"}
                                    </Button>
                                </Dropdown>
                            </Flex>
                        </Flex>
                    }
                >
                    <Input.TextArea
                        rows={12}
                        value={keys}
                        placeholder={lang.proxy_list_placeholder || "Danh sách proxy sẽ hiển thị ở đây..."}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            resize: 'none'
                        }}
                        readOnly
                    />
                    
                    {filteredData.length === 0 && (
                        <div style={{ 
                            textAlign: 'center', 
                            color: '#999', 
                            marginTop: 8 
                        }}>
                            <Text type="secondary">
                                {lang.no_proxy_message || "Không có proxy nào để hiển thị với filter hiện tại"}
                            </Text>
                        </div>
                    )}
                </Card>
            </div>
        </Modal>
    )
}

export default ModalExportHTTP